import React from 'react'
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router'
import type { LOCALIZATION_ADDRESS_INPUTS } from '@vtexlab/assisted-sales-localization'
import { useLocalization } from '@vtexlab/assisted-sales-localization'

import { CheckoutRoute } from '../../../../constants'
import {
  AddressComplementStep,
  AddressNeighborhoodStep,
  AddressNumberStep,
  AddressStreetStep,
} from '..'
import { useCheckout } from '../../../../state'
import { getNextAddressRoute } from '../../../../utils/getNextAddressRoute'
import { isEditMode } from '../../../../utils/verifyEditionMode'

export type AddressInputField = typeof LOCALIZATION_ADDRESS_INPUTS[number]

type AddressStep = {
  name: AddressInputField
  route: string
}

export const ADDRESS_STEPS: AddressStep[] = [
  {
    name: 'street',
    route: CheckoutRoute.AddressStreet,
  },
  {
    name: 'number',
    route: 'number',
  },
  {
    name: 'complement',
    route: 'complement',
  },
  {
    name: 'neighborhood',
    route: 'neighborhood',
  },
]

export const HomeDeliveryAddressSteps = () => {
  const { localizationConfig } = useLocalization()
  const checkout = useCheckout()
  const { address } = checkout.checkout.clientData
  const history = useHistory()
  const { path } = useRouteMatch()
  const emptySteps = getNextAddressRoute(address)
  const isEditing = isEditMode()

  const enabledSteps = ADDRESS_STEPS.filter((step) => {
    const isEmptyStep = emptySteps.find(
      (emptyStep) => emptyStep.name === step.name
    )

    const isEnabled =
      localizationConfig.input[step.name].enabled &&
      (isEmptyStep !== undefined || isEditing)

    if (step.name === 'neighborhood') {
      return (
        isEnabled &&
        localizationConfig.address.postalCodeMode !== 'state-neighborhood'
      )
    }

    return isEnabled
  })

  const getNextRoute = (currentStep: string) => {
    const nextStep =
      enabledSteps[
        enabledSteps.findIndex((step) => step.name === currentStep) + 1
      ]

    const editingParam = isEditing ? '?edit=true' : ''

    if (nextStep) {
      return `${path}/${nextStep.route}${editingParam}`
    }

    return `../${CheckoutRoute.ConfirmAdress}`
  }

  const checkStepIsEnabled = (step: string) => {
    return enabledSteps.find(({ name }) => name === step)
  }

  const handleStreetSubmit = (street: string | undefined) => {
    checkout.setClientDataAddressStreet(street)

    return history.push(getNextRoute('street'))
  }

  const handleAddressNumberSubmit = (number: string) => {
    checkout.setClientDataAddressNumber(number)

    return history.push(getNextRoute('number'))
  }

  const handleAddressComplementSubmit = (complement: string | undefined) => {
    checkout.setClientDataAddressComplement(complement)

    return history.push(getNextRoute('complement'))
  }

  const handleAddressNeighborhoodSubmit = (
    neighborhood: string | undefined
  ) => {
    checkout.setClientDataAddressNeighborhood(neighborhood)

    return history.push(getNextRoute('neighborhood'))
  }

  return (
    <Switch>
      {checkStepIsEnabled('street') && (
        <Route path={path} exact>
          <AddressStreetStep
            address={address}
            onStepSubmit={handleStreetSubmit}
          />
        </Route>
      )}
      {checkStepIsEnabled('number') && (
        <Route path={`${path}/number`}>
          <AddressNumberStep
            address={address}
            onStepSubmit={handleAddressNumberSubmit}
          />
        </Route>
      )}
      {checkStepIsEnabled('complement') && (
        <Route path={`${path}/complement`}>
          <AddressComplementStep
            address={address}
            complement={address.complement}
            onStepSubmit={handleAddressComplementSubmit}
          />
        </Route>
      )}
      {checkStepIsEnabled('neighborhood') && (
        <Route path={`${path}/neighborhood`}>
          <AddressNeighborhoodStep
            address={address}
            onStepSubmit={handleAddressNeighborhoodSubmit}
            neighborhoodApiValue={
              checkout.checkout.postalCodeApiResponse?.neighborhood ?? ''
            }
          />
        </Route>
      )}
      <Redirect to={`${CheckoutRoute.HomeDelivery}/confirm-address`} />
    </Switch>
  )
}
